import { useApiContext } from '@/api/context';
import { useMe } from '@/api/queries';
import { ROLES, type Role } from '@/domain/types';

const LABEL: Record<Role, string> = { viewer: 'Viewer', approver: 'Approver', admin: 'Admin' };

/** "No access" is a valid token in none of the three groups. */
export function roleLabel(role: Role | null): string {
  return role ? LABEL[role] : 'No access';
}

/**
 * Whether the person may do what `needs` names. This only decides which buttons show: the API
 * checks every request (CHG-050). Without sign-in (the mock, or an API with the switch off)
 * everything shows; until the API has said who is asking, nothing that writes does, so a button
 * never flashes and goes.
 */
export function useCan(needs: Role): boolean {
  const { mode } = useApiContext();
  const me = useMe().data;
  if (mode === 'mock') return true;
  if (!me) return false;
  if (me.auth === 'off') return true;
  return me.role !== null && ROLES.indexOf(me.role) >= ROLES.indexOf(needs);
}

export interface Person {
  name: string;
  email: string;
  role: Role | null;
  /** As the audit trail keeps them, "Name (email)" (the email alone if the token has no name): the API's own string. */
  recorded: string;
}

/** The signed-in person the API knows, or null with no sign-in (or before it has answered). */
export function usePerson(): Person | null {
  const me = useMe().data;
  if (me?.auth !== 'on') return null;
  return { name: me.name.trim(), email: me.email, role: me.role, recorded: me.recorded };
}
